// TextInput.tsx
import classNames from 'classnames';
import React, { ChangeEvent, useState } from 'react';
import { EyeIcons, EyeOffIcons } from '../../../icons/icons';

type TextInputProps = {
  label?: string;
  name: string;
  value: string;
  type: string;
  placeholder?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  onClick?: () => void;
  min?: string;
  max?: string;
  required?: boolean;
  className?: string;
  inputClassName?: string;
  variant?: 'default' | 'pill';
};

export const TextInput = ({
  label,
  name,
  value,
  type,
  placeholder = '',
  onChange,
  disabled = false,
  onClick,
  min,
  max,
  required = false,
  className,
  inputClassName,
  variant = 'default',
}: TextInputProps) => {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (show ? 'text' : 'password') : type;

  const isPill = variant === 'pill';

  return (
    <div className={classNames('w-full', className)}>
      { label && (
        <label
            htmlFor={name}
            className={classNames(
              'block mb-1.5 text-xs font-semibold text-slate-700 ml-0.5',
              isPill && 'text-slate-700 text-xs font-medium'
            )}
        >
          { label } { required && <span className='text-red-500'>*</span> }
        </label>
      ) }

      <div className='relative'>
        <input
            id={name}
            name={name}
            type={inputType}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            onClick={onClick}
            disabled={disabled}
            min={min}
            max={max}
            required={required}
            className={classNames(
              'w-full outline-none transition-all duration-200',
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              isPill
                ? 'h-11 px-4 text-sm bg-slate-100/90 border border-slate-200/80 rounded-2xl placeholder:text-slate-400 text-slate-900 focus:bg-white focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5'
                : 'h-9 px-3 text-[13px] bg-white border rounded-lg placeholder:text-slate-400 placeholder:text-xs border-slate-200 hover:border-slate-300 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10',
              disabled &&
                'cursor-not-allowed bg-slate-50 text-slate-400 border-slate-200',
              inputClassName
            )}
        />

        { isPassword && (
          <button
              type='button'
              onClick={() => setShow((s) => !s)}
              aria-label={show ? 'Hide password' : 'Show password'}
              className='absolute inset-y-0 right-3.5 flex items-center focus:outline-none'
          >
            { show ? (
              <EyeOffIcons className='w-4.5 h-4.5 text-slate-400 hover:text-slate-600 transition-colors' />
            ) : (
              <EyeIcons className='w-4.5 h-4.5 text-slate-400 hover:text-slate-600 transition-colors' />
            ) }
          </button>
        ) }
      </div>
    </div>
  );
};

